// ============================================================
// components/catalogo/ProductForm.jsx — Modal crear/editar producto
// ============================================================

import React, { useState } from 'react';

const ZONAS = ['Todas', 'Almirante Brown', 'Florencio Varela', 'Capitán Sarmiento'];
const TIPOS = ['Internet', 'Combo', 'TV', 'Adicional', 'Promocion'];

function ProductForm({ product, onSave, onClose }) {
  const isEdit = !!product;

  const [form, setForm] = useState({
    name: product?.name || '',
    family: product?.family || '',
    type: product?.type || 'Internet',
    speed: product?.speed || '-',
    tv: product?.tv || false,
    signals: product?.signals || 0,
    list_price: product?.list_price || 0,
    transfer_price: product?.transfer_price || 0,
    card_price: product?.card_price || 0,
    zones: product?.zones || ['Todas'],
    recommended_use: product?.recommended_use || '',
    description: product?.description || '',
    condiciones_contratacion: product?.condiciones_contratacion || '',
    display_order: product?.display_order || 0,
    active: product?.active !== undefined ? product.active : true,
    featured: product?.featured || false
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleZone = (zone) => {
    setForm(prev => {
      let newZones;
      if (zone === 'Todas') {
        newZones = prev.zones.includes('Todas') ? [] : ['Todas'];
      } else {
        const current = prev.zones.filter(z => z !== 'Todas');
        const exists = current.includes(zone);
        const next = exists ? current.filter(z => z !== zone) : [...current, zone];
        newZones = next.length ? next : ['Todas'];
      }
      return { ...prev, zones: newZones };
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    setError(null);

    // Normalizar campos numéricos vacíos a 0
    const datos = {
      ...form,
      list_price: Number(form.list_price) || 0,
      transfer_price: Number(form.transfer_price) || 0,
      card_price: Number(form.card_price) || 0,
      signals: Number(form.signals) || 0,
      display_order: Number(form.display_order) || 0
    };
    const result = await onSave(datos);
    setSaving(false);

    if (!result.ok) {
      setError(result.error || 'Error al guardar');
    }
  };

  return (
    <div className="cat-modal-overlay" onClick={onClose}>
      <div className="cat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cat-modal-header">
          <h2>{isEdit ? 'Editar producto' : 'Nuevo producto'}</h2>
          <button className="cat-btn-icon" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="cat-modal-body">
          {/* Nombre y Familia */}
          <div className="cat-form-row">
            <div className="cat-form-group">
              <label>Nombre del producto *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: 300 MB + TV"
              />
            </div>
            <div className="cat-form-group">
              <label>Familia</label>
              <input
                type="text"
                value={form.family}
                onChange={(e) => handleChange('family', e.target.value)}
                placeholder="Ej: MVSHUB + WeConnect"
              />
            </div>
          </div>

          {/* Tipo y Velocidad */}
          <div className="cat-form-row">
            <div className="cat-form-group">
              <label>Tipo</label>
              <select value={form.type} onChange={(e) => handleChange('type', e.target.value)}>
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="cat-form-group">
              <label>Velocidad</label>
              <input
                type="text"
                value={form.speed}
                onChange={(e) => handleChange('speed', e.target.value)}
                placeholder="Ej: 300 MB"
              />
            </div>
          </div>

          {/* TV */}
          <div className="cat-form-row">
            <div className="cat-form-group">
              <label className="cat-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.tv}
                  onChange={(e) => handleChange('tv', e.target.checked)}
                />
                Incluye TV
              </label>
            </div>
            {form.tv && (
              <div className="cat-form-group">
                <label>Señales TV</label>
                <input
                  type="number"
                  value={form.signals}
                  onChange={(e) => handleChange('signals', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
                />
              </div>
            )}
          </div>

          {/* Precios */}
          <div className="cat-form-section-title">Precios</div>
          <div className="cat-form-row triple">
            <div className="cat-form-group">
              <label>Precio lista</label>
              <input
                type="number"
                value={form.list_price}
                onChange={(e) => handleChange('list_price', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
              />
            </div>
            <div className="cat-form-group">
              <label>MP / Transferencia</label>
              <input
                type="number"
                value={form.transfer_price}
                onChange={(e) => handleChange('transfer_price', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
              />
            </div>
            <div className="cat-form-group">
              <label>Tarjeta crédito</label>
              <input
                type="number"
                value={form.card_price}
                onChange={(e) => handleChange('card_price', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
              />
            </div>
          </div>

          {/* Zonas */}
          <div className="cat-form-section-title">Localidades donde aplica</div>
          <div className="cat-zone-checks-form">
            {ZONAS.map(z => (
              <label
                key={z}
                className={`cat-zone-check ${form.zones.includes(z) ? 'checked' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={form.zones.includes(z)}
                  onChange={() => handleToggleZone(z)}
                />
                <span>{z}</span>
              </label>
            ))}
          </div>
          <p className="cat-form-hint">
            Si marcás "Todas", el plan queda disponible para cualquier localidad.
          </p>

          {/* Textos */}
          <div className="cat-form-group">
            <label>Uso sugerido (para la IA)</label>
            <input
              type="text"
              value={form.recommended_use}
              onChange={(e) => handleChange('recommended_use', e.target.value)}
              placeholder="Ej: Varios dispositivos, streaming y home office"
            />
          </div>
          <div className="cat-form-group">
            <label>Descripción comercial</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Descripción para el agente y la IA"
              rows={3}
            />
          </div>
          <div className="cat-form-group">
            <label>📝 Condiciones de contratación</label>
            <textarea
              value={form.condiciones_contratacion}
              onChange={(e) => handleChange('condiciones_contratacion', e.target.value)}
              placeholder="Ej: Con la confirmación del alta, el servicio queda activo. La baja será efectiva cumplidos los 30 días desde la fecha de activación."
              rows={3}
            />
            <p className="cat-form-hint">Este texto se incluye en el contexto IA para que informe las condiciones al cliente.</p>
          </div>

          {/* Opciones */}
          <div className="cat-form-row">
            <div className="cat-form-group">
              <label>Orden de visualización</label>
              <input
                type="number"
                value={form.display_order}
                onChange={(e) => handleChange('display_order', e.target.value === '' ? '' : (parseInt(e.target.value) || 0))}
              />
            </div>
            <div className="cat-form-group cat-form-toggles">
              <label className="cat-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => handleChange('active', e.target.checked)}
                />
                Activo
              </label>
              <label className="cat-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => handleChange('featured', e.target.checked)}
                />
                Destacado IA
              </label>
            </div>
          </div>

          {/* Error */}
          {error && <div className="cat-form-error">{error}</div>}
        </div>

        <div className="cat-modal-footer">
          <button className="cat-btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="cat-btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEdit ? 'Guardar cambios' : 'Crear producto')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductForm;
